// Per-device themes. Keep the legacy "glass" key for the refined Rose theme.
//
// Implements Req 18.1–18.5: per-device persistence (localStorage),
// Liquid Glass by default, opt-in system preference, ≥8 predefined accent colors. The
// accent color is applied by overwriting the `--ring` and `--primary`
// CSS variables on `:root`.
//
// We deliberately keep the persistence inside this module local to
// the web client — the storage adapter from `@link/storage` is
// reserved for cryptographic key material (Req 4.6 boundary). Theme
// preference is fine in `localStorage`.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ACCENT_COLORS, type AccentId } from "../theme";

export type ThemeMode = "strawberry" | "light" | "glass" | "liquid" | "dark" | "system";

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedMode: "light" | "dark";
  accent: AccentId;
  setMode: (mode: ThemeMode) => void;
  setAccent: (accent: AccentId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Decorative primitives may also be rendered outside the app (SSR, previews).
export function useLiquidGlassEnabled(): boolean {
  return useContext(ThemeContext)?.mode === "liquid";
}

const MODE_STORAGE_KEY = "link.theme.mode";
const ACCENT_STORAGE_KEY = "link.theme.accent";
const DEFAULT_MODE: ThemeMode = "liquid";
const DEFAULT_ACCENT: AccentId = "strawberry";

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return DEFAULT_MODE;
  const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
  if (
    raw === "strawberry" ||
    raw === "light" ||
    raw === "glass" ||
    raw === "liquid" ||
    raw === "dark" ||
    raw === "system"
  ) {
    return raw;
  }
  return DEFAULT_MODE;
}

function readStoredAccent(): AccentId {
  if (typeof window === "undefined") return DEFAULT_ACCENT;
  const raw = window.localStorage.getItem(ACCENT_STORAGE_KEY);
  const known = ACCENT_COLORS.find((c) => c.id === raw);
  return known?.id ?? DEFAULT_ACCENT;
}

function resolveSystemMode(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [accent, setAccentState] = useState<AccentId>(() => readStoredAccent());
  const [systemMode, setSystemMode] = useState<"light" | "dark">(() =>
    resolveSystemMode(),
  );

  // Track system preference changes so `mode === 'system'` reacts.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemMode(mql.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolvedMode: "light" | "dark" =
    mode === "system" ? systemMode : mode === "dark" ? "dark" : "light";

  // Apply the resolved mode to the documentElement so Tailwind's
  // `dark:` modifier and the CSS variables in `globals.css` swap.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (resolvedMode === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.dataset.theme = mode === "system" ? resolvedMode : mode;
    root.style.colorScheme = resolvedMode;
  }, [mode, resolvedMode]);

  // Apply the accent color by overriding the primary/ring CSS vars. Dark mode
  // deliberately uses a cool silver selection color so it stays visually
  // separate from the Strawberry theme without flattening into black/white.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const known = ACCENT_COLORS.find((c) => c.id === accent);
    if (known === undefined) return;
    const root = document.documentElement;
    const applied = mode === "glass" ? "338 48% 39%" : mode === "liquid" ? "0 0% 96%" : resolvedMode === "dark" ? "220 10% 74%" : known.hsl;
    root.style.setProperty("--primary", applied);
    // Pastel selection fills still need a strong keyboard-focus outline.
    root.style.setProperty("--ring", mode === "liquid" ? "0 0% 28%" : applied);
  }, [accent, resolvedMode, mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(MODE_STORAGE_KEY, next);
    }
  }, []);

  const setAccent = useCallback((next: AccentId) => {
    setAccentState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(ACCENT_STORAGE_KEY, next);
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedMode, accent, setMode, setAccent }),
    [mode, resolvedMode, accent, setMode, setAccent],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error("useTheme must be used inside a <ThemeProvider>");
  }
  return ctx;
}
